<template>
  <v-container fluid class="rv-report-page">
    <!-- Header Card -->
    <div class="rv-report-header">
      <div class="rv-report-header__top">
        <div class="rv-report-header__info">
          <h1 class="rv-report-header__title">Report Analysis</h1>
        </div>
      </div>
    </div>

    <AError :api="apiDownloadFinanceReport" />

    <!-- Tabs Navigation -->
    <div class="rv-report-tabs-wrapper">
      <v-tabs v-model="tab" background-color="transparent" class="rv-report-tabs" height="48">
        <!-- Generate Report tab hidden — only Finance Report enabled in Event Settings -->
        <!-- <v-tab :key="2" :href="`#tab-2`" class="rv-report-tab">
          <v-icon small class="mr-2">mdi-note-search</v-icon>
          Generate Report
        </v-tab> -->
        <v-tab :key="3" :href="`#tab-3`" class="rv-report-tab">
          <v-icon small class="mr-2">mdi-file-chart</v-icon>
          Finance Report
        </v-tab>
        <v-tab :key="4" :href="`#tab-4`" class="rv-report-tab">
          <v-icon small class="mr-2">mdi-microsoft-excel</v-icon>
          HRDC Event Details
        </v-tab>
      </v-tabs>
    </div>

    <!-- Tab Content -->
    <div class="rv-report-content">
      <v-tabs-items v-model="tab">
        <v-tab-item :key="3" :value="`tab-3`">
          <v-card flat class="rv-report-content-card">
            <finance-report
              :financeReportData="financeReportData"
              :financeReportError="financeReportError"
              @generateFinanceReport="generateFinanceReport"
            >
            </finance-report>
          </v-card>
        </v-tab-item>
        <v-tab-item :key="4" :value="`tab-4`">
          <v-card flat class="rv-report-content-card">
            <hrdc-event-details
              :isExporting="isExportingEventDetails"
              @exportExcel="exportHrdcEventDetails"
            ></hrdc-event-details>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <HrdcLoader
      :loadingDialog="apiDownloadFinanceReport.isLoading"
    ></HrdcLoader>
  </v-container>
</template>
<script>
import * as XLSX from "xlsx";
import { mapState } from "vuex";
import Api from "@/objects/api";
import Service from "@/objects/service";
import FinanceReport from "./FinanceReport";
import HrdcEventDetails from "./HrdcEventDetails";
import { getAllEventConfigs } from "@/services/hrdc_event/eventRegistry";

export default {
  components: {
    FinanceReport,
    HrdcEventDetails,
  },
  computed: mapState({
    auth: (state) => state.auth.data,
  }),
  props: ["params"],
  data: () => ({
    apiDownloadFinanceReport: new Api(),
    apiExportApplications: new Api(),
    service: new Service(),
    tab: "tab-3",
    loadingDialog: false,
    isExportingEventDetails: false,
    financeReportData: {
      searchRptDateRange: [],
      cutOffBy: null,
    },
    financeReportError: {
      dateRange: null,
      cutOffBy: null,
    },
    financeReportHeaders: [
      "Company Name",
      "Event Name",
      "Price",
      "Invoice No.",
      "Payment Received Date",
    ],
  }),
  async created() {
    // Resolve serviceKey: when accessed from hrdc_event_settings, use the first
    // active event module's serviceKey since all event modules share the same backend.
    const routeServiceKey = this.$route.params.serviceKey;
    if (routeServiceKey && routeServiceKey !== "hrdc_event_settings") {
      this.service.set("key", routeServiceKey);
    } else {
      const firstConfig = getAllEventConfigs()[0];
      if (firstConfig) {
        this.service.set("key", firstConfig.serviceKey);
      }
    }

    this.apiDownloadFinanceReport.setUrl(
      `${this.$service[this.service.key]}/v1/en/console/report/download-finance-report`
    );
    this.apiDownloadFinanceReport.setMethod("post");

    // HRDC Event Details — exports ALL event applications (across every event
    // type) into one Excel workbook. The backend endpoint is not scoped by
    // hrdcType, so this naturally returns all HRDC event data.
    this.apiExportApplications.setUrl(
      `${this.$service[this.service.key]}/v1/en/console/application/export-all-applications-excel`
    );
    this.apiExportApplications.setMethod("get");
  },
  methods: {
    redirectBack() {
      if (this.$route.params.serviceKey === "hrdc_event_settings") {
        this.$router
          .push({
            name: "PageServiceDashboard",
            params: { serviceKey: "hrdc_event_settings" },
          })
          .catch(() => {});
        return;
      }
      this.$router.go(-1);
    },
    clearErrorMsg() {
      for (let error in this.financeReportError) {
        this.financeReportError[error] = null;
      }
    },
    async exportHrdcEventDetails() {
      if (this.isExportingEventDetails) return;
      this.isExportingEventDetails = true;

      await this.$axios
        .get(this.apiExportApplications.url, {
          responseType: "blob",
          timeout: 0, // large export — do not let the client time out
          headers: {
            Authorization: `Bearer ${this.$store.state.auth.data.token}`,
          },
        })
        .then((response) => {
          // Prefer the filename the server set; fall back to a stamped name.
          let fileName = `HRDC_Event_Applications_${this.$moment().format(
            "YYYYMMDD_HHmmss"
          )}.xlsx`;
          const disposition = response.headers["content-disposition"];
          if (disposition) {
            const match = disposition.match(
              /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i
            );
            if (match && match[1]) {
              fileName = decodeURIComponent(match[1]);
            }
          }

          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.log(error);
          this.$store.dispatch("showMessage", {
            message:
              "Failed to export HRDC event details, kindly contact administrator",
            messageType: "error",
            timeout: 2500,
          });
        })
        .finally(() => {
          this.isExportingEventDetails = false;
        });
    },
    async generateFinanceReport(financeReportData) {
      this.loadingDialog = !this.loadingDialog;

      this.apiDownloadFinanceReport.setParams({
        cutOffBy: financeReportData.cutOffBy,
        dateRange: financeReportData.searchRptDateRange,
      });
      this.apiDownloadFinanceReport.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();

          const { status, data } = response;
          if (status == "NO_DATA_FOUND") {
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "info",
              timeout: 2000,
            });
          }

          if (!status) {
            Object.keys(data).forEach((key) => {
              this.financeReportError[key] = data[key];
            });
          }

          if (status === true) {
            let worksheetData = [];
            for (const item of data) {
              worksheetData.push({
                companyName: item.oldCompanyName,
                eventName: item.eventName,
                price: "RM " + item.totalPrice.toLocaleString(),
                hrdcInvoiceNo: item.manualInvoiceToHrdc,
                paymentReceivedDate: item.paymentReceivedDate
                  ? this.$moment(item.paymentReceivedDate).format("YYYY-MM-DD")
                  : null,
              });
            }

            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(
              workbook,
              worksheet,
              "Finance Claim Basis"
            );

            XLSX.utils.sheet_add_aoa(worksheet, [this.financeReportHeaders], {
              origin: "A1",
            });

            var wscols = [
              { wch: 20 },
              { wch: 20 },
              { wch: 20 },
              { wch: 20 },
              { wch: 20 },
            ];

            worksheet["!cols"] = wscols;

            XLSX.writeFile(workbook, `Finance Claim Basis.xlsx`);
          }

          this.loadingDialog = !this.loadingDialog;
        } catch (err) {
          console.log(err);
        }
      });

      this.apiDownloadFinanceReport.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .rv-report-page {
  background: var(--app-page);
}

.dark-mode .rv-report-header {
  background: var(--app-elevated);
}

.dark-mode .rv-report-back-btn {
  border: 1px solid var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .rv-report-back-btn:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .rv-report-tabs-wrapper {
  background: var(--app-elevated);
}

.dark-mode .rv-report-tabs-wrapper >>> .v-tab {
  color: var(--app-text-muted) !important;
}

.dark-mode .rv-report-tabs-wrapper >>> .v-tab:hover {
  color: var(--app-text) !important;
  background: var(--app-hover) !important;
}

.dark-mode .rv-report-tabs-wrapper >>> .v-tab--active {
  background: var(--app-card) !important;
}

.dark-mode .rv-report-content {
  background: var(--app-card);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE LAYOUT
   ============================================ */
.rv-report-page {
  background: #d8dce2;
  font-family: 'Lato', sans-serif;
  min-height: 100vh;
  padding: 24px 28px !important;
}

/* ============================================
   HEADER
   ============================================ */
.rv-report-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 20px;
  overflow: hidden;
}

.rv-report-header__top {
  display: flex;
  align-items: center;
  padding: 22px 28px;
}

.rv-report-back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  background: #fff;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.rv-report-back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.rv-report-header__info {
  flex: 1;
  min-width: 0;
}

.rv-report-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  letter-spacing: -0.2px;
}

/* Title kept its light near-black and disappeared into the elevated header
   surface in dark mode. */
.dark-mode .rv-report-header__title {
  color: var(--app-text);
}

/* ============================================
   TABS
   ============================================ */
.rv-report-tabs-wrapper {
  background: #eaecef;
  border-radius: 14px 14px 0 0;
  padding: 6px 16px 0;
}

.rv-report-tabs-wrapper >>> .v-tabs-slider-wrapper {
  color: #0ea5e9 !important;
}

.rv-report-tabs-wrapper >>> .v-tab {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  color: #64748b !important;
  min-width: auto !important;
  padding: 0 18px !important;
  border-radius: 10px 10px 0 0;
  transition: all 0.2s ease;
}

.rv-report-tabs-wrapper >>> .v-tab:hover {
  color: #334155 !important;
  background: rgba(15, 23, 42, 0.08) !important;
}

.rv-report-tabs-wrapper >>> .v-tab--active {
  color: #0ea5e9 !important;
  background: #fff !important;
}

.rv-report-tabs-wrapper >>> .v-tab--active .v-icon {
  color: #0ea5e9 !important;
}

/* ============================================
   CONTENT
   ============================================ */
.rv-report-content {
  background: #fff;
  border-radius: 0 0 14px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

.rv-report-content-card {
  background: transparent !important;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 768px) {
  .rv-report-page {
    padding: 12px 10px !important;
  }

  .rv-report-header__top {
    padding: 16px 18px;
  }

  .rv-report-header__title {
    font-size: 1rem;
  }

  .rv-report-tabs-wrapper >>> .v-tab {
    font-size: 0.72rem !important;
    padding: 0 10px !important;
  }
}

@media (max-width: 480px) {
  .rv-report-tabs-wrapper >>> .v-tab {
    font-size: 0.68rem !important;
    padding: 0 8px !important;
  }
}
</style>
